import { createSlice } from '@reduxjs/toolkit';
import { LABELGENAPI } from '../../../API';
import { logFrontendActivity } from '../../General/activityLogger';

export const lineItemsLogSlice = createSlice({
    name: 'lineItemsLog',
    initialState: {
        data: [],
        chartData: [], 
        topSkus: [],
        skusList: [], // ─── ADDED TO INITIAL STATE ───
        meta: {
            page: 1,
            per_page: 10,
            start_date: "",
            end_date: "",
            event: "ALL",
            source: "ALL",
            search: "",
            sorting: JSON.stringify([{
                field: 'created_at',
                sort: 'desc',
            }])
        },
        loading: false,
        chartLoading: false, 
        hasErrors: false,
        notification: { show: false, messageContent: "", messageType: "success" }
    },
    reducers: {
        getLogsData: (state) => {
            return {
                ...state,
                loading: true,
                hasErrors: false
            }
        },
        getLogsDataSuccess: (state, action) => {
            return {
                ...state,
                data: action && action.payload && action.payload.data && action.payload.data.data && action.payload.data.data.data,
                meta: action && action.payload && action.payload.data && action.payload.data.data,
                // ─── EXTRACT AND STORE UNIQUE SKUS DIRECTLY FROM THE METADATA LAYER ───
                skusList: action && action.payload && action.payload.data && action.payload.data.skus || [],
                hasErrors: false,
                loading: false
            }
        },
        getLogsDataFailure: (state, action) => {
            return {
                ...state,
                loading: false,
                hasErrors: true,
                message: action && action.payload && action.payload.data && action.payload.data.message
            }
        },
        getChartData: (state) => {
            return {
                ...state,
                chartLoading: true
            }
        },
        getChartDataSuccess: (state, action) => {
            return {
                ...state,
                chartData: action && action.payload && action.payload.data && action.payload.data.data?.chart || [],
                chartTopSkus: action && action.payload && action.payload.data && action.payload.data.data?.top_skus || [], // Stores keys list array
                chartLoading: false
            }
        },
        getChartDataFailure: (state) => {
            return {
                ...state,
                chartData: [],
                chartLoading: false
            }
        },
        getTopSkusMetricsStart: (state) => {
            state.topSkusLoading = true;
        },
        getTopSkusMetricsSuccess: (state, action) => {
            state.topSkusLoading = false;
            state.topSkus = action.payload;
        },
        getTopSkusMetricsFailure: (state) => {
            state.topSkusLoading = false;
            state.topSkus = [];
        },
        resetLogs: (state) => {
            return {
                ...state,
                loading: false,
                chartLoading: false,
                chartData: [],
                topSkus : [],
                skusList: [], // Clean array lists on view drop overrides
                hasErrors: false,
                notification: { show: false, messageContent: "", messageType: "success" }
            }
        }
    }
});

export const { 
    getLogsData, 
    getLogsDataSuccess, 
    getLogsDataFailure, 
    getChartData, 
    getChartDataSuccess, 
    getChartDataFailure,
    getTopSkusMetricsStart,
    getTopSkusMetricsSuccess,
    getTopSkusMetricsFailure, 
    resetLogs 
} = lineItemsLogSlice.actions;

export const fetchLineItemsLogsData = (meta) => {
    const qs = Object.keys(meta)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(meta[key])}`)
        .join('&');

    return (dispatch) => {
        dispatch(getLogsData());
        try {
            LABELGENAPI
                .get('/lineitemsLogsInfo?' + qs)
                .then(function (response) {
                    dispatch(getLogsDataSuccess(response));
                })
                .catch(function (error) {
                    dispatch(getLogsDataFailure(error.response));
                });
        } catch (error) {
            dispatch(getLogsDataFailure());
        }
    };
};

export const fetchLineItemsChartMetrics = (meta) => {
    const { page, per_page, sorting, ...chartMeta } = meta;
    const qs = Object.keys(chartMeta)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(chartMeta[key])}`)
        .join('&');

    return (dispatch) => {
        dispatch(getChartData());
        try {
            LABELGENAPI
                .get('/lineitemsLogsInfo/chart-metrics?' + qs)
                .then(function (response) {
                    dispatch(getChartDataSuccess(response));
                })
                .catch(function (error) {
                    dispatch(getChartDataFailure(error.response));
                });
        } catch (error) {
            dispatch(getChartDataFailure());
        }
    };
};

export const exportLineItemsLogsCSV = (userid, meta) => dispatch => {
    const qs = Object.keys(meta)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(meta[key])}`)
        .join('&');

    const logRef = crypto.randomUUID();
    
    try {
        LABELGENAPI.get('/lineitemsLogsInfo/export?' + qs, { 
            responseType: 'blob',
            headers: { 'x-log-ref': logRef } 
        })
        .then(function (response) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `lineitems_log_report_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            logFrontendActivity({
                group: 'REPORTS',
                subgroup: 'LINEITEMS_LOG',
                action: 'EXPORT_SUCCESS',
                request_data: meta,
                response_data: { message: "CSV binary blob file generated successfully." },
                log_ref: logRef,
                userid: userid
            });
        }).catch(function (error) {
            logFrontendActivity({
                group: 'REPORTS',
                subgroup: 'LINEITEMS_LOG',
                action: 'EXPORT_FAILURE',
                request_data: meta,
                response_data: error.response?.data || {},
                log_ref: logRef,
                userid: userid
            });
        });
    } catch (error) {
        console.error(error);
    }
};

export const fetchLineItemsTopStats = (meta) => {
    // Filter pagination out so metric parameter caches remain pure
    const { page, per_page, sorting, ...statsMeta } = meta;
    const qs = Object.keys(statsMeta)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(statsMeta[key])}`)
        .join('&');

    return (dispatch) => {
        dispatch(getTopSkusMetricsStart());
        try {
            LABELGENAPI
                .get('/lineitemsLogsInfo/top-stats?' + qs)
                .then(function (response) {
                    dispatch(getTopSkusMetricsSuccess(response.data?.data || []));
                })
                .catch(function () {
                    dispatch(getTopSkusMetricsFailure());
                });
        } catch (error) {
            dispatch(getTopSkusMetricsFailure());
        }
    };
};

export const resetLogsState = () => dispatch => {
    dispatch(resetLogs());
};

export default lineItemsLogSlice.reducer;